import Network from "../network/network";
import Logger, { Loglevel } from "../util/Logger";
import Entity from "../Entities/Entity";

export default abstract class BaseService<TEntity extends Entity> {
    protected abstract getServiceName(): string;
    protected abstract getOdataRoute(): string;

    protected abstract mapDtoToEntity(dto: any): TEntity;

    protected readonly network: Network;
    protected readonly logger: Logger;

    constructor(network: Network, logger: Logger) {
        this.network = network;
        this.logger = logger;
    }

    public async getItems(filter?: string): Promise<TEntity[]> {
        return (await this.tryGetItems(filter)) ?? [];
    }

    /** The items, or null if the request failed - by then the reason is in the log. */
    protected tryGetItems = async (filter?: string): Promise<TEntity[]> => {
        try {
            const response = await this.network.requestApi(`${this.getOdataRoute()}${this.getRouteExtenstions(filter)}`);
            return BaseService.itemsOf(response).map(dto => this.mapDtoToEntity(dto));
        } catch (e) {
            this.logger.log(Loglevel.Error, `${this.getServiceName()} failed to get items`, e);
            return null;
        }
    };

    public getCount = async (filter: string): Promise<number> => {
        try {
            const response = await this.network.requestApi(`${this.getOdataRoute()}?$filter=${filter}&$top=0&$count=true`);
            BaseService.ensureNoError(response);
            return response["@odata.count"] ?? 0;
        } catch (e) {
            this.logger.log(Loglevel.Error, `${this.getServiceName()} failed to get the item count`, e);
            return 0;
        }
    };

    public getItem = async (id: number): Promise<TEntity> => {
        try {
            const response = await this.network.requestApi(`${this.getOdataRoute()}(${id})`);
            BaseService.ensureNoError(response);
            return this.mapDtoToEntity(response);
        } catch (e) {
            this.logger.log(Loglevel.Error, `${this.getServiceName()} failed to get single item`, e);
            return null;
        }
    };

    /** The items a collection answer carries; throws the reason when it carries an error instead of a list. */
    private static itemsOf(response: any): any[] {
        BaseService.ensureNoError(response);
        if (!Array.isArray(response?.value)) {
            throw new Error("the answer carries no item list");
        }
        return response.value;
    }

    /** Throws what an OData error answer reports, which is what a refused request sends instead of data. */
    protected static ensureNoError(response: any): void {
        const error = response?.error;
        if (error) {
            throw new Error(`the endpoint reported ${error.code || "an error"}: ${error.message || "no reason given"}`);
        }
    }

    private getRouteExtenstions = (filter: string): string => {
        let extension = "?";
        if (filter) {
            extension += `$filter=${filter}`;
        }
        if (this.getExpands().length) {
            extension += `${extension.length > 1 ? "&" : ""}`;
            extension += `$expand=${this.getExpands().join(",")}`;
        }
        if (this.getSelects().length) {
            extension += `${extension.length > 1 ? "&" : ""}`;
            extension += `$select=${this.getSelects().join(",")}`;
        }
        return extension;
    };

    protected getExpands = (): string[] => {
        return [];
    };

    protected getSelects = (): string[] => {
        return [];
    };
}
